import React, { useContext } from 'react'
import Box from '@material-ui/core/Box'
import { useFormikContext } from 'formik'

import { CashflowEventsContext } from '../../context'
import messages from '../../messages.json'
import { EVENT_TYPES } from '../../reducer'
import { Formik, Form } from 'formik'
import { InputFieldWithLabel, NumberFieldWithLabel, CheckboxWithLabel, Select } from '../../../../components/Form/Fields'
import { formValidationSchema } from '../../helpers/validations'
import { Button } from '@components/Button'
import { SelectWithLabel } from '@components/Form/Fields'
import { Hider } from '@components/HOCs/Hider'
import { EventMessages } from '../../Shared'
import { getFormEventTypeFromState } from '../../helpers/stateHelper'
import { frequencyOptions, submitJointCashflowEvent } from '../../helpers/formHelper'
import { R } from '@utils/currency'
import { annualInflationValueLabel, annualInflationCheckboxLabel } from './labels'
import { percentage } from '../../../../utils/currency'

export const JointEventForm = () => {
  const context = useContext(CashflowEventsContext)
  const { state, actions } = context
  const { eventAttributes, isFormValid, eventType, assumptions, loading, participants } = state
  const { back, formDelete } = actions
  const { inflation } = assumptions

  const deleteLabel = getDeleteLabelFromEventType(eventAttributes)
  const sourceLabel = getSourceLabelFromEventType()

  return (
    <Formik
      onSubmit={submitJointCashflowEvent()}
      initialValues={initialValues(eventAttributes, participants)}
      validationSchema={formValidationSchema()}
      render={({ isValid, values, errors }) => (
        <Form>
          <InputFieldWithLabel name='name' label={sourceLabel} />
          <NumberFieldWithLabel name='amount' label='Amount' />
          <SplitFields eventType={eventType} />
          <EventYearFields errors={errors} values={values} />
          <AnnualInflationFields errors={errors} values={values} eventType={eventType} inflation={inflation} />
          <EventMessages show={!isFormValid} />

          <Box className='grid grid-cols-2 gap-4'>
            <Button className='button button--secondary' onClick={back}>Back</Button>
            <Button className='button button--primary' type='submit' disabled={!isValid || loading}>
              {loading ? 'Loading...' : 'Add to plan'}
            </Button>
            <Button disabled={loading} className='button button--tertiary col-span-2' onClick={formDelete(eventAttributes, context)}>
              {deleteLabel}
            </Button>
          </Box>
        </Form>
      )} />
  )
}

const AnnualInflationFields = ({ errors, values, eventType, inflation }) => {
  const checkboxLabel = annualInflationCheckboxLabel[eventType]
  const valueLabel = annualInflationValueLabel[eventType]
  return (
    <div className="form__annual-inflation-fields">
      <Box mb='1rem'>
        <Hider withTransition collapse
          show={values.apply_annual_inflation}
          render={() =>
            <NumberFieldWithLabel name='annual_inflation' label={valueLabel} />
          } />
        <CheckboxWithLabel name='apply_annual_inflation' label={checkboxLabel} />
        {!values.apply_annual_inflation &&
          <DefaultInflationMessage eventType={eventType} inflation={inflation} />
        }
      </Box>
    </div>
  )
}

const DefaultInflationMessage = ({ eventType, inflation }) => {
  return (
    <Box ml='1rem'>
      <p>
        By default, an annual {eventType === EVENT_TYPES.INCOME ? 'increase ' : 'inflation '}
        of <strong>{eventType === EVENT_TYPES.INCOME ? 0 : inflation * 100}%</strong> is applied to this {eventType === EVENT_TYPES.INCOME ? 'income' : 'expense'}
      </p>
    </Box>
  )
}


const EventYearFields = ({ errors, values }) =>
  <div className='form__age-fields'>
    <NumberFieldWithLabel name='start_year'
      label={getStartAgeLabel(values.recurring)}
    />
    <Hider withTransition collapse
      show={values.recurring}
      render={() =>
        <NumberFieldWithLabel name='end_year' label={messages.form.recurringEndYear} />
      } />
    <Box display='flex'>
      <CheckboxWithLabel name='recurring' label='Recurring Event' />
      <Hider withTransition collapse
        show={showFrequencyField(errors, values)}
        render={() =>
          <Select name='frequency' options={frequencyOptions(values, errors)} />
        } />
    </Box>
  </div>

const showFrequencyField = (errors, values) =>
  !(errors.end_age || errors.start_age) && values.recurring

const SplitFields = ({ eventType }) => {
  const { values, setFieldValue } = useFormikContext()
  const { distribution_type, joint_cashflow_event_distributions_attributes } = values

  return (
    <div className='md:w-1/2 mx-4 my-4'>
      <SelectWithLabel
        className='min-w-fit mb-4'
        name='distribution_type'
        label={`${eventType} split`}
        options={[
          {
            name: 'Custom', value: 'custom'
          },
          {
            name: 'Proportional by income', value: 'proportional_by_income'
          },
          {
            name: 'Proportional by disctretionary investments', value: 'proportional_by_discretionary_investments'
          }
        ]}
      />
      <Hider withTransition
        show={distribution_type === 'custom'}
        render={() =>
          <div className='flex gap-4'>
            {joint_cashflow_event_distributions_attributes.map(({ client_name }, idx) =>
              <div className='flex-1'>
                <NumberFieldWithLabel
                  label={`${client_name}'s portion(%)`}
                  name={`joint_cashflow_event_distributions_attributes[${idx}].value`}
                  placeholder='0'
                  numberFormatProps={{
                    isAllowed: (value) => {
                      const { floatValue } = value

                      return R(floatValue).value <= 100
                    }
                  }}
                  // User convenience: when one value is changed, the other is automatically updated
                  onChange={(e) => {
                    const { value } = e.target
                    const remainder = 100 - value
                    setFieldValue(`joint_cashflow_event_distributions_attributes[${idx}].value`, R(value).value || null)
                    setFieldValue(`joint_cashflow_event_distributions_attributes[${idx === 0 ? 1 : 0}].value`, R(remainder).value || null)
                  }}
                />
              </div>
            )}
          </div>
        }
      />
    </div>
  )
}

const initialValues = (eventAttributes, participants) => ({
  joint: true,
  id: eventAttributes ? eventAttributes.id : null,
  name: eventAttributes ? eventAttributes.name : '',
  amount: eventAttributes ? eventAttributes.amount : '',
  start_year: eventAttributes ? eventAttributes.start_year : '',
  recurring: eventAttributes ? (eventAttributes.end_year !== eventAttributes.start_year) : false,
  end_year: eventAttributes ? eventAttributes.end_year : '',
  frequency: eventAttributes ? eventAttributes.frequency : 1,
  apply_annual_inflation: eventAttributes ? (eventAttributes.annual_inflation !== null) : false,
  annual_inflation: eventAttributes ? eventAttributes.annual_inflation * 100 : '',
  distribution_type: eventAttributes ? eventAttributes.distribution_type : 'custom',
  status: eventAttributes ? eventAttributes.status : 'active',
  joint_cashflow_event_distributions_attributes: eventAttributes
    ? eventAttributes.joint_cashflow_event_distributions_attributes
      .map((distribution) => ({
        ...distribution,
        value: percentage(distribution.value).value
      }))
    : participants.map((participant) => ({
      client_id: participant.id,
      client_name: participant.preferredName,
      current_age: participant.currentAge,
      value: 50,
      cashflow_event_attributes: {
        retirement_scenario_id: participant.scenarioId,
      }
    }))
})

const getDeleteLabelFromEventType = (eventAttributes) => {
  const formEventType = getFormEventTypeFromState()
  if (eventAttributes) {
    return formEventType === EVENT_TYPES.INCOME
      ? messages.form.deleteIncomeEvent
      : messages.form.deleteExpenseEvent
  } else {
    return messages.form.backToCashflowEvents
  }
}

const getSourceLabelFromEventType = () => {
  const formEventType = getFormEventTypeFromState()
  return formEventType === EVENT_TYPES.INCOME
    ? messages.form.sourceLabelIncomeEvent
    : messages.form.sourceLabelExpenseEvent
}

const getStartAgeLabel = (recurring) => {
  return recurring
    ? messages.form.recurringStartYear
    : messages.form.nonRecurringStartYear
}
